import React, { useState, useEffect } from 'react';
import { RefreshCw, Filter, Trophy } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicResults = () => {
  const [results, setResults] = useState([]);
  const [stages, setStages] = useState([]);
  const [classes, setClasses] = useState([]);
  const [driveTypes, setDriveTypes] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedDriveTypes, setSelectedDriveTypes] = useState([]);
  const [selectedClubmans, setSelectedClubmans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventName, setEventName] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [eventId, setEventId] = useState(null);

  // Get event_id from URL params or use current event
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlEventId = params.get('event_id');
    if (urlEventId) {
      setEventId(urlEventId);
    } else {
      fetchCurrentEvent();
    }
  }, []);

  useEffect(() => {
    if (eventId) {
      fetchResults();
      // Auto-refresh every 15 seconds
      const interval = setInterval(fetchResults, 15000);
      return () => clearInterval(interval);
    }
  }, [eventId, selectedClasses, selectedDriveTypes, selectedClubmans]);

  const fetchCurrentEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/current/public`);
      if (response.data?.id) {
        setEventId(response.data.id);
        setEventName(response.data.name);
      } else {
        setError('No event currently active');
      }
    } catch (err) {
      setError('Unable to load event');
    }
  };

  const fetchResults = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ event_id: eventId });
      // Support multiple class filters
      if (selectedClasses.length > 0) {
        selectedClasses.forEach(cls => params.append('class_filter', cls));
      }
      // Support multiple drive type filters
      if (selectedDriveTypes.length > 0) {
        selectedDriveTypes.forEach(dt => params.append('drive_type_filter', dt));
      }
      // Support multiple clubmans filters
      if (selectedClubmans.length > 0) {
        selectedClubmans.forEach(c => params.append('clubmans_filter', c));
      }
      
      const response = await axios.get(`${API}/live-results/public?${params}`);
      setResults(response.data.results || []);
      setStages(response.data.stages || []);
      setClasses(response.data.classes || []);
      setDriveTypes(response.data.drive_types || []);
      if (response.data.event_name) setEventName(response.data.event_name);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Live results are not publicly available');
      } else {
        setError('Unable to load results');
      }
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedClasses([]);
    setSelectedDriveTypes([]);
    setSelectedClubmans([]);
  };

  const hasFilters = selectedClasses.length > 0 || selectedDriveTypes.length > 0 || selectedClubmans.length > 0;

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <Trophy size={48} className="mx-auto text-zinc-600 mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Live Timing</h1>
          <p className="text-zinc-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      {/* Header */}
      <div className="bg-[#18181b] border-b border-[#27272a] px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Trophy className="text-[#facc15]" size={28} />
              <div>
                <h1 className="text-2xl font-black text-[#facc15] tracking-wider" data-testid="public-results-title">LIVE TIMING</h1>
                <p className="text-zinc-400">{eventName}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-zinc-500">
              {lastUpdated && `Updated: ${lastUpdated.toLocaleTimeString()}`}
            </div>
            <button 
              onClick={fetchResults}
              className="p-2 rounded bg-[#27272a] hover:bg-[#3f3f46] transition-colors"
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin text-[#facc15]' : 'text-white'} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-[#18181b] border border-[#27272a] rounded-lg p-3">
          <Filter size={16} className="text-zinc-400" />
          
          {/* Class Filter - Multi-select */}
          {classes.length > 0 && (
            <div className="relative group">
              <button
                className="bg-[#27272a] border border-[#3f3f46] text-white rounded px-3 py-2 text-sm min-w-[100px] text-left"
                data-testid="class-filter"
              >
                {selectedClasses.length === 0 ? 'All Classes' : `Classes (${selectedClasses.length})`}
              </button>
              <div className="absolute hidden group-hover:block z-50 bg-[#27272a] border border-[#3f3f46] rounded shadow-lg mt-1 min-w-[150px] max-h-[200px] overflow-y-auto">
                {classes.map(cls => (
                  <label key={cls} className="flex items-center gap-2 px-3 py-2 hover:bg-[#3f3f46] cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={selectedClasses.includes(cls)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedClasses([...selectedClasses, cls]);
                        } else {
                          setSelectedClasses(selectedClasses.filter(c => c !== cls));
                        }
                      }}
                      className="rounded bg-[#18181b] border-zinc-600"
                    />
                    {cls}
                  </label>
                ))}
                {selectedClasses.length > 0 && (
                  <button
                    onClick={() => setSelectedClasses([])}
                    className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-[#3f3f46] border-t border-[#3f3f46]"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Drive Type Filter - Multi-select */}
          {driveTypes.length > 0 && (
            <div className="relative group">
              <button
                className="bg-[#27272a] border border-[#3f3f46] text-white rounded px-3 py-2 text-sm min-w-[100px] text-left"
                data-testid="drive-type-filter"
              >
                {selectedDriveTypes.length === 0 ? 'All Drive Types' : `Drive (${selectedDriveTypes.length})`}
              </button>
              <div className="absolute hidden group-hover:block z-50 bg-[#27272a] border border-[#3f3f46] rounded shadow-lg mt-1 min-w-[120px]">
                {driveTypes.map(dt => (
                  <label key={dt} className="flex items-center gap-2 px-3 py-2 hover:bg-[#3f3f46] cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={selectedDriveTypes.includes(dt)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDriveTypes([...selectedDriveTypes, dt]);
                        } else {
                          setSelectedDriveTypes(selectedDriveTypes.filter(d => d !== dt));
                        }
                      }}
                      className="rounded bg-[#18181b] border-zinc-600"
                    />
                    {dt}
                  </label>
                ))}
                {selectedDriveTypes.length > 0 && (
                  <button
                    onClick={() => setSelectedDriveTypes([])}
                    className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-[#3f3f46] border-t border-[#3f3f46]"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Clubmans Filter - Multi-select */}
          <div className="relative group">
            <button
              className="bg-[#27272a] border border-[#3f3f46] text-white rounded px-3 py-2 text-sm min-w-[100px] text-left"
              data-testid="clubmans-filter"
            >
              {selectedClubmans.length === 0 ? 'All Entrants' : `Clubmans (${selectedClubmans.length})`}
            </button>
            <div className="absolute hidden group-hover:block z-50 bg-[#27272a] border border-[#3f3f46] rounded shadow-lg mt-1 min-w-[140px]">
              <label className="flex items-center gap-2 px-3 py-2 hover:bg-[#3f3f46] cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={selectedClubmans.includes('yes')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedClubmans([...selectedClubmans, 'yes']);
                    } else {
                      setSelectedClubmans(selectedClubmans.filter(c => c !== 'yes'));
                    }
                  }}
                  className="rounded bg-[#18181b] border-zinc-600"
                />
                Clubmans Only
              </label>
              <label className="flex items-center gap-2 px-3 py-2 hover:bg-[#3f3f46] cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={selectedClubmans.includes('no')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedClubmans([...selectedClubmans, 'no']);
                    } else {
                      setSelectedClubmans(selectedClubmans.filter(c => c !== 'no'));
                    }
                  }}
                  className="rounded bg-[#18181b] border-zinc-600"
                />
                Non-Clubmans
              </label>
              {selectedClubmans.length > 0 && (
                <button
                  onClick={() => setSelectedClubmans([])}
                  className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-[#3f3f46] border-t border-[#3f3f46]"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {hasFilters && (
            <button 
              onClick={clearFilters}
              className="text-xs text-zinc-400 hover:text-white underline"
            >
              Clear filters
            </button>
          )}

          {hasFilters && (
            <span className="text-sm text-zinc-400 ml-auto">
              Showing {results.length} entries
            </span>
          )}
        </div>

        {/* Results Table */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="results-table">
              <thead className="bg-[#27272a]">
                <tr>
                  <th className="px-4 py-3 text-left text-[#facc15] font-bold">Pos</th>
                  <th className="px-4 py-3 text-left text-[#facc15] font-bold">No.</th>
                  <th className="px-4 py-3 text-left text-[#facc15] font-bold">Crew</th>
                  <th className="px-4 py-3 text-left text-[#facc15] font-bold">Class</th>
                  <th className="px-4 py-3 text-left text-[#facc15] font-bold">Drive</th>
                  <th className="px-4 py-3 text-left text-[#facc15] font-bold">Vehicle</th>
                  {stages.map(stage => (
                    <th key={stage.id} className="px-4 py-3 text-center text-[#facc15] font-bold whitespace-nowrap">
                      SS{stage.number}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-[#facc15] font-bold">Pen.</th>
                  <th className="px-4 py-3 text-center text-[#facc15] font-bold">Total</th>
                  <th className="px-4 py-3 text-center text-[#facc15] font-bold">Gap</th>
                </tr>
              </thead>
              <tbody>
                {loading && results.length === 0 ? (
                  <tr>
                    <td colSpan={10 + stages.length} className="px-4 py-12 text-center">
                      <RefreshCw className="animate-spin mx-auto text-[#facc15]" size={24} />
                      <p className="text-zinc-500 mt-2">Loading results...</p>
                    </td>
                  </tr>
                ) : results.length === 0 ? (
                  <tr>
                    <td colSpan={10 + stages.length} className="px-4 py-12 text-center text-zinc-500">
                      No results available yet
                    </td>
                  </tr>
                ) : (
                  results.map((result, idx) => (
                    <tr 
                      key={result.car_number} 
                      className={`border-t border-[#27272a] transition-colors hover:bg-[#1f1f23] ${
                        idx === 0 ? 'bg-gradient-to-r from-[#facc15]/20 to-transparent' : ''
                      } ${result.status === 'dnf' || result.status === 'dns' ? 'opacity-50' : ''}`}
                      data-testid={`result-row-${result.car_number}`}
                    >
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                          idx === 0 ? 'bg-[#facc15] text-black' : 
                          idx === 1 ? 'bg-zinc-400 text-black' : 
                          idx === 2 ? 'bg-amber-700 text-white' : 
                          'bg-[#27272a] text-white'
                        }`}>
                          {result.position}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-[#facc15] font-bold text-lg">{result.car_number}</td>
                      <td className="px-4 py-3">
                        <div className="text-white font-semibold">{result.driver}</div>
                        {result.co_driver && <div className="text-zinc-400 text-xs">{result.co_driver}</div>}
                        {result.entrant && <div className="text-zinc-500 text-xs italic">{result.entrant}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-[#27272a] rounded text-xs font-medium">
                          {result.class_name}
                        </span>
                        {result.clubmans && <span className="ml-1 px-1 bg-blue-500/20 text-blue-400 text-xs rounded" title="Clubmans">C</span>}
                      </td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{result.drive_type}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{result.car_model}</td>
                      {stages.map(stage => {
                        const stageKey = `stage_${stage.number}`;
                        const time = result.stage_times?.[stageKey] || '-';
                        return (
                          <td key={stage.id} className="px-4 py-3 text-center font-mono text-white">
                            {time}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center font-mono text-orange-400">
                        {result.total_penalty !== '-' ? result.total_penalty : '-'}
                      </td>
                      <td className="px-4 py-3 text-center font-mono font-bold text-white text-base">
                        {result.total_time}
                      </td>
                      <td className="px-4 py-3 text-center font-mono text-zinc-400">
                        {result.time_to_leader}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <span className="px-1 bg-blue-500/20 text-blue-400 rounded">C</span>
            <span>Clubmans entry</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-orange-400">+00:30</span>
            <span>Time penalty</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-zinc-600">
          <p>Powered by Rally Timing • Results update automatically every 15 seconds</p>
        </div>
      </div>
    </div>
  );
};

export default PublicResults;
